<template>
  <v-container fluid>
    <v-card class="status-card mb-4">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-heart-pulse</v-icon>
        System-Status
        <v-spacer></v-spacer>
        <v-chip :color="overallStatus.color" size="small">{{
          overallStatus.text
        }}</v-chip>
      </v-card-title>

      <v-card-text>
        <div class="status-overview mb-4">
          <v-row>
            <v-col cols="12" sm="4">
              <v-card variant="outlined" class="status-item">
                <v-card-title class="py-2 px-4">
                  <v-icon :color="serviceStatus.web.color" class="mr-2"
                    >mdi-web</v-icon
                  >
                  Webserver
                </v-card-title>
                <v-card-text class="py-2 px-4">
                  <v-progress-linear
                    :model-value="serviceStatus.web.uptime"
                    :color="serviceStatus.web.color"
                    height="8"
                    rounded
                  ></v-progress-linear>
                  <div class="d-flex justify-space-between mt-1">
                    <span>Uptime</span>
                    <span>{{ serviceStatus.web.uptime }}%</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" sm="4">
              <v-card variant="outlined" class="status-item">
                <v-card-title class="py-2 px-4">
                  <v-icon :color="serviceStatus.db.color" class="mr-2"
                    >mdi-database</v-icon
                  >
                  Datenbank
                </v-card-title>
                <v-card-text class="py-2 px-4">
                  <v-progress-linear
                    :model-value="serviceStatus.db.uptime"
                    :color="serviceStatus.db.color"
                    height="8"
                    rounded
                  ></v-progress-linear>
                  <div class="d-flex justify-space-between mt-1">
                    <span>Uptime</span>
                    <span>{{ serviceStatus.db.uptime }}%</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" sm="4">
              <v-card variant="outlined" class="status-item">
                <v-card-title class="py-2 px-4">
                  <v-icon :color="serviceStatus.websocket.color" class="mr-2"
                    >mdi-webhook</v-icon
                  >
                  WebSocket
                </v-card-title>
                <v-card-text class="py-2 px-4">
                  <v-progress-linear
                    :model-value="serviceStatus.websocket.uptime"
                    :color="serviceStatus.websocket.color"
                    height="8"
                    rounded
                  ></v-progress-linear>
                  <div class="d-flex justify-space-between mt-1">
                    <span>Uptime</span>
                    <span>{{ serviceStatus.websocket.uptime }}%</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-4"></v-divider>

        <div class="performance-metrics mb-4">
          <h3 class="text-h6 mb-3">Performance-Metriken</h3>
          <v-row>
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="performance-card">
                <v-card-title class="py-2 px-4 d-flex align-center">
                  <v-icon color="info" class="mr-2">mdi-memory</v-icon>
                  CPU-Auslastung
                  <v-spacer></v-spacer>
                  <span>{{ cpuUsage }}%</span>
                </v-card-title>
                <v-card-text class="py-2 px-4">
                  <v-progress-linear
                    :model-value="cpuUsage"
                    :color="getCpuColor(cpuUsage)"
                    height="10"
                    rounded
                    striped
                  ></v-progress-linear>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card variant="outlined" class="performance-card">
                <v-card-title class="py-2 px-4 d-flex align-center">
                  <v-icon color="info" class="mr-2"
                    >mdi-database-outline</v-icon
                  >
                  Speicherauslastung
                  <v-spacer></v-spacer>
                  <span>{{ memoryUsage }}%</span>
                </v-card-title>
                <v-card-text class="py-2 px-4">
                  <v-progress-linear
                    :model-value="memoryUsage"
                    :color="getMemoryColor(memoryUsage)"
                    height="10"
                    rounded
                    striped
                  ></v-progress-linear>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-4"></v-divider>

        <div class="status-history">
          <h3 class="text-h6 mb-3">Status-Historie</h3>
          <v-timeline side="end" density="compact">
            <v-timeline-item
              v-for="(event, index) in statusHistory"
              :key="index"
              :dot-color="event.color"
              size="small"
            >
              <div class="d-flex align-center">
                <v-chip size="small" :color="event.color" class="mr-2">{{
                  event.service
                }}</v-chip>
                <span class="text-caption text-medium-emphasis">{{
                  event.date
                }}</span>
              </div>
              <div>{{ event.message }}</div>
            </v-timeline-item>
          </v-timeline>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="system-info-card">
      <v-card-title>
        <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
        System-Informationen
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-list>
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-tag-outline</v-icon>
                </template>
                <v-list-item-title>Version</v-list-item-title>
                <v-list-item-subtitle>{{
                  systemInfo.version
                }}</v-list-item-subtitle>
              </v-list-item>

              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-server</v-icon>
                </template>
                <v-list-item-title>Server</v-list-item-title>
                <v-list-item-subtitle>{{
                  systemInfo.server
                }}</v-list-item-subtitle>
              </v-list-item>

              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-database</v-icon>
                </template>
                <v-list-item-title>Datenbank</v-list-item-title>
                <v-list-item-subtitle>{{
                  systemInfo.database
                }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-col>

          <v-col cols="12" md="6">
            <v-list>
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-clock-outline</v-icon>
                </template>
                <v-list-item-title>Laufzeit</v-list-item-title>
                <v-list-item-subtitle>{{
                  systemInfo.uptime
                }}</v-list-item-subtitle>
              </v-list-item>

              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-flash</v-icon>
                </template>
                <v-list-item-title>PHP-Version</v-list-item-title>
                <v-list-item-subtitle>{{
                  systemInfo.php
                }}</v-list-item-subtitle>
              </v-list-item>

              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-web</v-icon>
                </template>
                <v-list-item-title>Active Users</v-list-item-title>
                <v-list-item-subtitle>{{
                  systemInfo.activeUsers
                }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useAuthStore } from "@/stores/auth";
import axios from "axios";
import { API_BASE_URL } from "@/config";
import { useToast } from "vue-toastification";
import { useRouter } from "vue-router";

const router = useRouter();
const authStore = useAuthStore();
const toast = useToast();
const loading = ref(false);
const refreshInterval = ref(null);

// Status der Dienste
const serviceStatus = ref({
  web: { status: "online", uptime: 99.8, color: "success" },
  db: { status: "online", uptime: 99.5, color: "success" },
  websocket: { status: "online", uptime: 98.2, color: "success" },
});

// Performance-Metriken
const cpuUsage = ref(42);
const memoryUsage = ref(58);

// Gesamtstatus berechnen
const overallStatus = computed(() => {
  const services = Object.values(serviceStatus.value);

  if (services.some((s) => s.status === "offline")) {
    return { text: "Teilweise Ausfälle", color: "error" };
  }

  if (services.some((s) => s.status === "degraded")) {
    return { text: "Eingeschränkt", color: "warning" };
  }

  return { text: "Alle Systeme funktionieren", color: "success" };
});

// Farbberechnung für Metriken
const getCpuColor = (usage) => {
  if (usage > 80) return "error";
  if (usage > 60) return "warning";
  return "success";
};

const getMemoryColor = (usage) => {
  if (usage > 85) return "error";
  if (usage > 70) return "warning";
  return "success";
};

// Beispielhafte Status-Historie
const statusHistory = ref([
  {
    service: "Datenbank",
    status: "recovered",
    color: "success",
    date: "21.03.2025 18:45",
    message: "Datenbank-Performance wiederhergestellt",
  },
  {
    service: "Datenbank",
    status: "degraded",
    color: "warning",
    date: "21.03.2025 18:30",
    message: "Erhöhte Datenbank-Latenz festgestellt",
  },
  {
    service: "WebSocket",
    status: "recovered",
    color: "success",
    date: "20.03.2025 14:15",
    message: "WebSocket-Dienst wieder online",
  },
  {
    service: "WebSocket",
    status: "offline",
    color: "error",
    date: "20.03.2025 14:00",
    message: "WebSocket-Dienst vorübergehend nicht verfügbar",
  },
  {
    service: "System",
    status: "info",
    color: "info",
    date: "19.03.2025 10:00",
    message: "Geplante Wartung abgeschlossen",
  },
]);

// System-Informationen
const systemInfo = ref({
  version: "v1.5.6",
  server: "Apache/2.4.41 (Ubuntu)",
  database: "MySQL 8.0.31",
  uptime: "23 Tage, 4 Stunden",
  php: "PHP 8.3.15",
  activeUsers: "42 aktive Benutzer",
});

// Daten von der API laden
const fetchSystemStatus = async () => {
  loading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}admin/system_status.php?action=get_status`, {
      withCredentials: true,
    });

    if (response.data && response.data.status === "success") {
      // Erhaltene Daten in die reaktiven Variablen einpflegen
      const data = response.data.data;

      // Dienststatus aktualisieren
      if (data.services) {
        serviceStatus.value = data.services;
      }

      // Performance-Metriken aktualisieren
      if (data.performance) {
        cpuUsage.value = data.performance.cpu || cpuUsage.value;
        memoryUsage.value = data.performance.memory || memoryUsage.value;
      }

      // Systeminfo aktualisieren
      if (data.system) {
        systemInfo.value = {
          ...systemInfo.value,
          ...data.system,
        };
      }

      // Statusverlauf aktualisieren
      if (data.history && Array.isArray(data.history)) {
        statusHistory.value = data.history;
      }
    }
  } catch (error) {
    console.error("Fehler beim Laden der Systemstatus-Daten:", error);
    toast.error("Fehler beim Laden der Systemstatus-Daten");
  } finally {
    loading.value = false;
  }
};

// Lebenszyklus-Hooks
onMounted(() => {
  // Zugriffskontrolle - nur Admin/Developer/Super-Admin dürfen zugreifen
  if (
    !authStore.user?.admin &&
    !authStore.user?.developer &&
    !authStore.user?.super_admin
  ) {
    toast.error("Sie haben keine Berechtigung für diese Seite");
    router.push("/dashboard");
    return;
  }

  // Initial Daten laden
  fetchSystemStatus();

  // Regelmäßige Aktualisierung der Daten einrichten (alle 30 Sekunden)
  refreshInterval.value = setInterval(fetchSystemStatus, 30000);
});

onUnmounted(() => {
  // Aufräumen - Intervall stoppen
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value);
  }
});
</script>

<style scoped>
.status-card,
.system-info-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.status-item,
.performance-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.status-item:hover,
.performance-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.v-timeline-item {
  margin-bottom: 8px;
}
</style>
